import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import toast from "../../utils/toast";
import sampleTypeConfigService from "../../services/sampleTypeConfigService";

const normalizeTemplateRangeRecord = (range) => {
  if (!range || typeof range !== "object") return range;
  return {
    ...range,
    text_range: range.text_range ?? null,
    remarks: range.remarks ?? null,
  };
};

export const fetchSampleTypeTubeTypes = createAsyncThunk(
  "sampleTypeConfig/fetchSampleTypeTubeTypes",
  async (sampleTypeId, { rejectWithValue }) => {
    try {
      const res = await sampleTypeConfigService.getSampleTypeTubeTypes(sampleTypeId);
      // controller uses successResponse => { data: { sample_type_id, tube_type_ids }, ... }
      const payload = res?.data?.data ?? res?.data;
      return payload;
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to fetch sample type tube types";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const saveSampleTypeTubeTypes = createAsyncThunk(
  "sampleTypeConfig/saveSampleTypeTubeTypes",
  async ({ sampleTypeId, tubeTypeIds }, { rejectWithValue }) => {
    try {
      const res = await sampleTypeConfigService.setSampleTypeTubeTypes(
        sampleTypeId,
        tubeTypeIds
      );
      const payload = res?.data?.data ?? res?.data;
      toast.success(res?.data?.message || "Saved tube types");
      return payload;
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to save sample type tube types";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const fetchSampleTypeParameters = createAsyncThunk(
  "sampleTypeConfig/fetchSampleTypeParameters",
  async (sampleTypeId, { rejectWithValue }) => {
    try {
      const res = await sampleTypeConfigService.getSampleTypeParameters(sampleTypeId);
      const payload = res?.data?.data ?? res?.data;
      return { sampleTypeId, items: Array.isArray(payload) ? payload : [] };
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to fetch sample type parameters";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const createSampleTypeParameter = createAsyncThunk(
  "sampleTypeConfig/createSampleTypeParameter",
  async ({ sampleTypeId, data }, { rejectWithValue }) => {
    try {
      const res = await sampleTypeConfigService.createSampleTypeParameter(
        sampleTypeId,
        data
      );
      const payload = res?.data?.data ?? res?.data;
      toast.success(res?.data?.message || "Parameter created");
      return { sampleTypeId, item: payload };
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to create sample type parameter";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const updateSampleTypeParameter = createAsyncThunk(
  "sampleTypeConfig/updateSampleTypeParameter",
  async ({ parameterId, data }, { rejectWithValue }) => {
    try {
      const res = await sampleTypeConfigService.updateSampleTypeParameter(
        parameterId,
        data
      );
      const payload = res?.data?.data ?? res?.data;
      toast.success(res?.data?.message || "Parameter updated");
      return { parameterId, patch: payload };
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to update sample type parameter";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const deleteSampleTypeParameter = createAsyncThunk(
  "sampleTypeConfig/deleteSampleTypeParameter",
  async (parameterId, { rejectWithValue }) => {
    try {
      const res = await sampleTypeConfigService.deleteSampleTypeParameter(parameterId);
      toast.success(res?.data?.message || "Parameter deleted");
      return parameterId;
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to delete sample type parameter";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const fetchTemplateRanges = createAsyncThunk(
  "sampleTypeConfig/fetchTemplateRanges",
  async (parameterId, { rejectWithValue }) => {
    try {
      console.log("[sampleTypeConfig] fetchTemplateRanges request", { parameterId });
      const res = await sampleTypeConfigService.getTemplateRangesByParameter(parameterId);
      const payload = res?.data?.data ?? res?.data;
      const items = Array.isArray(payload) ? payload.map(normalizeTemplateRangeRecord) : [];
      console.log("[sampleTypeConfig] fetchTemplateRanges response", {
        parameterId,
        payload,
        items,
        res
      });
      return { parameterId, items };
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to fetch reference ranges";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const createTemplateRange = createAsyncThunk(
  "sampleTypeConfig/createTemplateRange",
  async ({ parameterId, data }, { rejectWithValue }) => {
    try {
      console.log("[sampleTypeConfig] createTemplateRange request", {
        parameterId,
        data,
      });
      const res = await sampleTypeConfigService.createTemplateRange(parameterId, data);
      const payload = res?.data?.data ?? res?.data;
      const item = normalizeTemplateRangeRecord(payload);
      console.log("[sampleTypeConfig] createTemplateRange response", {
        parameterId,
        payload,
        item,
        res
      });
      toast.success(res?.data?.message || "Range created");
      return { parameterId, item };
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to create reference range";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const updateTemplateRange = createAsyncThunk(
  "sampleTypeConfig/updateTemplateRange",
  async ({ rangeId, data }, { rejectWithValue }) => {
    try {
      console.log("[sampleTypeConfig] updateTemplateRange request", {
        rangeId,
        data,
      });
      const res = await sampleTypeConfigService.updateTemplateRange(rangeId, data);
      const payload = res?.data?.data ?? res?.data;
      const patch = normalizeTemplateRangeRecord(payload);
      console.log("[sampleTypeConfig] updateTemplateRange response", {
        rangeId,
        payload,
        patch,
      });
      toast.success(res?.data?.message || "Range updated");
      return { rangeId, patch };
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to update reference range";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const deleteTemplateRange = createAsyncThunk(
  "sampleTypeConfig/deleteTemplateRange",
  async (rangeId, { rejectWithValue }) => {
    try {
      const res = await sampleTypeConfigService.deleteTemplateRange(rangeId);
      toast.success(res?.data?.message || "Range deleted");
      return rangeId;
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to delete reference range";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

const sampleTypeConfigSlice = createSlice({
  name: "sampleTypeConfig",
  initialState: {
    tubeTypeIdsBySampleTypeId: {},
    parametersBySampleTypeId: {},
    rangesByParameterId: {},
    loading: false,
    error: null,
  },
  reducers: {
    clearSampleTypeConfigError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSampleTypeTubeTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSampleTypeTubeTypes.fulfilled, (state, action) => {
        state.loading = false;
        const { sample_type_id, tube_type_ids } = action.payload || {};
        if (sample_type_id !== undefined) {
          state.tubeTypeIdsBySampleTypeId[sample_type_id] = tube_type_ids || [];
        }
      })
      .addCase(fetchSampleTypeTubeTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(saveSampleTypeTubeTypes.fulfilled, (state, action) => {
        const { sample_type_id, tube_type_ids } = action.payload || {};
        if (sample_type_id !== undefined) {
          state.tubeTypeIdsBySampleTypeId[sample_type_id] = tube_type_ids || [];
        }
      })

      .addCase(fetchSampleTypeParameters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSampleTypeParameters.fulfilled, (state, action) => {
        state.loading = false;
        state.parametersBySampleTypeId[action.payload.sampleTypeId] =
          action.payload.items;
      })
      .addCase(fetchSampleTypeParameters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createSampleTypeParameter.fulfilled, (state, action) => {
        const { sampleTypeId, item } = action.payload;
        const prev = state.parametersBySampleTypeId[sampleTypeId] || [];
        state.parametersBySampleTypeId[sampleTypeId] = [...prev, item];
      })

      .addCase(updateSampleTypeParameter.fulfilled, (state, action) => {
        const { parameterId, patch } = action.payload;
        Object.keys(state.parametersBySampleTypeId).forEach((stId) => {
          const prev = state.parametersBySampleTypeId[stId] || [];
          const idx = prev.findIndex((p) => p.id === Number(parameterId) || p.id === parameterId);
          if (idx === -1) return;
          const next = [...prev];
          next[idx] = { ...next[idx], ...patch };
          state.parametersBySampleTypeId[stId] = next;
        });
      })

      .addCase(deleteSampleTypeParameter.fulfilled, (state, action) => {
        const id = action.payload;
        Object.keys(state.parametersBySampleTypeId).forEach((stId) => {
          state.parametersBySampleTypeId[stId] = (state.parametersBySampleTypeId[stId] || []).filter(
            (p) => p.id !== id
          );
        });
        delete state.rangesByParameterId[id];
      })

      .addCase(fetchTemplateRanges.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTemplateRanges.fulfilled, (state, action) => {
        state.loading = false;
        state.rangesByParameterId[action.payload.parameterId] = action.payload.items;
      })
      .addCase(fetchTemplateRanges.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createTemplateRange.fulfilled, (state, action) => {
        const { parameterId, item } = action.payload;
        const prev = state.rangesByParameterId[parameterId] || [];
        state.rangesByParameterId[parameterId] = [...prev, item];
      })

      .addCase(updateTemplateRange.fulfilled, (state, action) => {
        const { rangeId, patch } = action.payload;
        Object.keys(state.rangesByParameterId).forEach((paramId) => {
          const prev = state.rangesByParameterId[paramId] || [];
          const idx = prev.findIndex((r) => r.id === Number(rangeId) || r.id === rangeId);
          if (idx === -1) return;
          const next = [...prev];
          next[idx] = { ...next[idx], ...patch };
          state.rangesByParameterId[paramId] = next;
        });
      })

      .addCase(deleteTemplateRange.fulfilled, (state, action) => {
        const id = action.payload;
        Object.keys(state.rangesByParameterId).forEach((paramId) => {
          state.rangesByParameterId[paramId] = (state.rangesByParameterId[paramId] || []).filter(
            (r) => r.id !== id
          );
        });
      });
  },
});

export const { clearSampleTypeConfigError } = sampleTypeConfigSlice.actions;
export default sampleTypeConfigSlice.reducer;
